import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type { Employees } from 'modules/employees/store/collections/employees'
import { useFormAccessGroup } from 'modules/surveys/hooks/useAccessGroup'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import type SurveyForm from 'modules/surveys/store/models/Form'

interface Props {
  form: SurveyForm
  employees: Employees
}

export default function AddSupervisorModal({ form, employees }: Props) {
  const { navigation } = useNavigation()

  const [userId, setUserId] = useState<string | null | undefined>(null)
  const [running, setRunning] = useState(false)
  const { loading: loadingFormAccessGroup, accessGroup } = useFormAccessGroup(form)
  const navigate = useNavigate()

  const onClose = () => {
    const route = navigation.engagement.surveys.id.configuration.fullPath({
      id: form.attributes.id,
    })
    navigate(route)
  }

  const submit = async (closePortal: ClosePortalFn) => {
    if (!accessGroup) return

    setRunning(true)

    const { success } = await asyncAction(() =>
      formAccessGroupMemberships.create({
        access_id: userId,
        access_group_id: form.attributes.access_group_id,
        form_id: form.attributes.id,
      })
    )
    setRunning(false)

    if (success) closePortal()
  }

  const memberIds = formAccessGroupMemberships.toArray().map((m) => m.attributes.access_id) ?? []

  return (
    <Modal
      title={i18n.t('surveys.form.add_manager.title')}
      description={i18n.t('surveys.form.add_manager.description')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={!userId || running} type="primary" aria-label="Add Editor button" submit>
          {i18n.t('surveys.form.add_manager.submit')}
        </Button>
      }
    >
      {loadingFormAccessGroup ? (
        <Loading />
      ) : (
        <EmployeeSelectorV2
          useAccessId
          value={userId ?? undefined}
          onChange={setUserId}
          forcedFilters={{ excludeIds: memberIds }}
        />
      )}
    </Modal>
  )
}
